import type { AudioService } from "../../audio/ports/AudioService";
import type { CanvasOverlayStore } from "../../canvas-overlay/CanvasOverlayStore";
import type { GameTransitionPort } from "./GameTransitionPort";
import type { Log } from "@slot/presentation";

/**
 * Runs the game-transition overlay: show, play, hide. The motion belongs to the game's view
 * (`panel.playTransition()`: a frame sequence, a tween, a Spine or nothing), so this presenter
 * serves any game.
 *
 * Emits `game.transition` as the visual starts; a title maps it to its stinger in
 * `GameAudioOverrides.mapping`, else nothing plays.
 */
export class GameTransitionPresenter {
  private readonly panel: GameTransitionPort;
  private readonly audioService: AudioService;
  private readonly canvasOverlayStore: CanvasOverlayStore;
  private readonly log: Log;

  public constructor(input: { panel: GameTransitionPort; audioService: AudioService; canvasOverlayStore: CanvasOverlayStore; log: Log }) {
    this.panel = input.panel;
    this.audioService = input.audioService;
    this.canvasOverlayStore = input.canvasOverlayStore;
    this.log = input.log;

    this.panel.hide();
  }

  public async presentTransition(): Promise<void> {
    this.log("[GameTransition] play");
    const releaseOverlay = this.canvasOverlayStore.open();
    try {
      this.panel.show();
      // Fire the transition stinger just before the visual kicks off so audio
      // and animation start in the same frame. Fire-and-forget; the visual's
      // await is what gates `hide()` below, not the sound's duration.
      this.audioService.presentation.emit({ id: "game.transition" });
      await this.panel.playTransition();
      this.panel.hide();
    } finally {
      releaseOverlay();
    }
    this.log("[GameTransition] done");
  }
}
